import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { getKpis, type DashboardKpis } from "../../api/dashboard";
import { ApiClientError } from "../../api/types";
import { CollapsibleSection } from "../../components/document/CollapsibleSection";
import { useAuth } from "../../context/AuthContext";
import { ROLE_LABELS } from "../../lib/rbac";
import { AccountBalanceTiles } from "./components/AccountBalanceTiles";
import { AnalyticsChartSuite } from "./components/AnalyticsChartSuite";
import { DashboardSessionHeader } from "./components/DashboardSessionHeader";
import { KpiGrid } from "./components/KpiGrid";
import { StrategicAiPanel } from "./components/StrategicAiPanel";
import { currentMonthDashboardRange } from "./lib/date-range";

function formatOrganizationName(orgId: string): string {
  const normalized = orgId.trim();
  if (!normalized) {
    return "Global Enterprise";
  }

  const slug = normalized.replace(/^org_/i, "").replace(/[_-]+/g, " ").trim();
  if (!slug) {
    return normalized;
  }

  return slug.replace(/\b\w/g, (character) => character.toUpperCase());
}

function resolvePrimaryRoleLabel(
  roleLabels: string[],
  roles: number[],
): string {
  if (roleLabels.length > 0 && roleLabels[0]?.trim()) {
    return roleLabels[0];
  }

  const primaryRole = roles[0];
  if (primaryRole !== undefined && primaryRole in ROLE_LABELS) {
    return ROLE_LABELS[primaryRole as keyof typeof ROLE_LABELS];
  }

  return "SuperAdmin";
}

export function DashboardPage() {
  const { t } = useTranslation();
  const { profile } = useAuth();

  const defaultRange = useMemo(() => currentMonthDashboardRange(), []);

  const [startDate, setStartDate] = useState(defaultRange.startDate);
  const [endDate, setEndDate] = useState(defaultRange.endDate);
  const [appliedRange, setAppliedRange] = useState(defaultRange);
  const [kpis, setKpis] = useState<DashboardKpis | null>(null);
  const [isLoadingKpis, setIsLoadingKpis] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const loadKpis = useCallback(async () => {
    setIsLoadingKpis(true);
    setError(null);
    try {
      const data = await getKpis(appliedRange.startDate, appliedRange.endDate);
      setKpis(data);
    } catch (err) {
      setKpis(null);
      setError(
        err instanceof ApiClientError
          ? err.message
          : err instanceof Error
            ? err.message
            : t("dashboard.kpis_load_failed"),
      );
    } finally {
      setIsLoadingKpis(false);
    }
  }, [appliedRange.endDate, appliedRange.startDate, t]);

  useEffect(() => {
    void loadKpis();
  }, [loadKpis, refreshKey]);

  function handleApplyRange() {
    setAppliedRange({
      startDate: startDate.trim(),
      endDate: endDate.trim(),
    });
    setRefreshKey((value) => value + 1);
  }

  function handleResetRange() {
    setStartDate(defaultRange.startDate);
    setEndDate(defaultRange.endDate);
    setAppliedRange(defaultRange);
    setRefreshKey((value) => value + 1);
  }

  if (!profile) {
    return null;
  }

  const organizationName = formatOrganizationName(profile.orgId);
  const roleLabel = resolvePrimaryRoleLabel(profile.roleLabels, profile.roles);

  return (
    <div className="erp-page">
      <DashboardSessionHeader
        organizationName={organizationName}
        username={profile.displayName || profile.login}
        roleLabel={roleLabel}
      />

      <CollapsibleSection
        title={t("dashboard.section_date_range")}
        subtitle={t("dashboard.date_range_hint")}
        defaultOpen={false}
      >
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-5">
          <div>
            <label htmlFor="dashboard-start-date" className="erp-label">
              {t("dashboard.filter_start_date")}
            </label>
            <input
              id="dashboard-start-date"
              type="text"
              dir="ltr"
              className="erp-input"
              value={startDate}
              onChange={(event) => setStartDate(event.target.value)}
              placeholder="01/05/2026 00:00"
            />
          </div>

          <div>
            <label htmlFor="dashboard-end-date" className="erp-label">
              {t("dashboard.filter_end_date")}
            </label>
            <input
              id="dashboard-end-date"
              type="text"
              dir="ltr"
              className="erp-input"
              value={endDate}
              onChange={(event) => setEndDate(event.target.value)}
              placeholder="31/05/2026 23:59"
            />
          </div>

          <div className="flex flex-wrap items-end gap-1 sm:col-span-2 lg:col-span-3">
            <button
              type="button"
              onClick={handleApplyRange}
              className="rounded bg-godo-navy px-3 py-1 text-xs font-semibold text-white hover:bg-[#003d8f]"
            >
              {t("dashboard.apply_range")}
            </button>
            <button
              type="button"
              onClick={handleResetRange}
              className="rounded border border-slate-300 px-3 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50"
            >
              {t("dashboard.reset_range")}
            </button>
            <span className="text-[10px] text-slate-500">{t("dashboard.date_format_hint")}</span>
          </div>
        </div>
      </CollapsibleSection>

      {error && (
        <div
          role="alert"
          className="rounded border border-red-200 bg-red-50 px-2 py-1 text-xs text-red-700"
        >
          {error}
        </div>
      )}

      <AccountBalanceTiles kpis={kpis} isLoading={isLoadingKpis} />

      <KpiGrid kpis={kpis} isLoading={isLoadingKpis} />

      <CollapsibleSection
        title={t("dashboard.section_ai")}
        subtitle={t("dashboard.section_ai_hint")}
        defaultOpen={false}
      >
        <StrategicAiPanel
          startDate={appliedRange.startDate}
          endDate={appliedRange.endDate}
          refreshKey={refreshKey}
        />
      </CollapsibleSection>

      <CollapsibleSection
        title={t("dashboard.section_charts")}
        subtitle={t("dashboard.section_charts_hint")}
        defaultOpen={false}
      >
        <AnalyticsChartSuite kpis={kpis} isLoading={isLoadingKpis} />
      </CollapsibleSection>
    </div>
  );
}
